import React, { useState } from 'react'
import './FranchiseDetailsModal.css'
import franchiseService from '../services/franchiseService' // Import service
import { useAuth } from '../context/AuthContext' // Import Auth for pre-filling

const FranchiseDetailsModal = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [step, setStep] = useState('details'); // 'details' | 'form'

  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.mobile || '',
    businessName: '',
    location: '',
    message: ''
  });

  if (!isOpen) return null

  // Reset state when closing/opening
  const handleClose = () => {
    setSuccess(false);
    setStep('details');
    setError('');
    onClose();
  }

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  }

  const handleRequestClick = () => {
    // Check if user already has a role other than 'user'
    if (user && user.role && user.role !== 'user') {
      setError(`You already have the role: ${user.role}. You cannot apply for additional franchise access.`);
      return;
    }
    setStep('form');
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await franchiseService.createRequest(formData);
      setSuccess(true);
      // Optional: close after delay
      // setTimeout(handleClose, 2000);
    } catch (err) {
      setError(err.message || 'Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="franchise-modal-overlay" onClick={handleClose}>
      <div className="franchise-modal-content" onClick={e => e.stopPropagation()}>
        <button className="close-btn" onClick={handleClose}>✕</button>

        <div className="modal-header">
          <div className="icon-badge">🍟</div>
          <h2>Franchise Restaurants</h2>
          <p>Order & Billing System</p>
        </div>

        <div className="modal-body">
          {success ? (
            <div className="success-state">
              <div className="success-icon">✅</div>
              <h3>Request Received!</h3>
              <p>Thank you for your interest. Our team will contact you shortly at <strong>{formData.email}</strong>.</p>
              <button className="btn-primary full-width" onClick={handleClose}>Close</button>
            </div>
          ) : step === 'form' ? (
            <form className="request-form" onSubmit={handleSubmit}>
              <h3>Request Access</h3>
              <p className="form-subtitle">Please provide your details below.</p>

              {error && <div className="error-message">{error}</div>}

              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  disabled
                  className="input-disabled"
                  placeholder="Enter your name"
                />
              </div>

              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  disabled
                  className="input-disabled"
                  placeholder="name@example.com"
                />
              </div>

              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  disabled
                  className="input-disabled"
                  placeholder="Your contact number"
                />
              </div>

              <div className="form-group">
                <label>Restaurant/Business Name <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  type="text"
                  name="businessName"
                  value={formData.businessName}
                  onChange={handleInputChange}
                  placeholder="Restaurant or Cafe Name"
                  required
                />
              </div>

              <div className="form-group">
                <label>Location/Address <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  placeholder="City, State or Full Address"
                  required
                />
              </div>

              <div className="form-group">
                <label>Additional Message (Optional)</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Any additional information you'd like to share..."
                  rows="3"
                />
              </div>

              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setStep('details')}>Back</button>
                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? 'Sending...' : 'Submit Request'}
                </button>
              </div>
            </form>
          ) : (
            <>
              <div className="feature-intro">
                <p>Each franchise must subscribe to access the QR Menu Ordering & Billing System. This system simplifies restaurant operations by enabling customers to order and pay digitally.</p>
              </div>

              <div className="features-list">
                <h3>Key Features</h3>

                <div className="feature-item">
                  <span className="feature-icon">📲</span>
                  <div className="feature-text">
                    <strong>QR Code Menu Access</strong>
                    <p>Customers can scan QR codes to view digital menus instantly.</p>
                  </div>
                </div>

                <div className="feature-item">
                  <span className="feature-icon">🛍️</span>
                  <div className="feature-text">
                    <strong>Digital Ordering</strong>
                    <p>Orders placed directly from customer's device without waiting.</p>
                  </div>
                </div>

                <div className="feature-item">
                  <span className="feature-icon">🍳</span>
                  <div className="feature-text">
                    <strong>Kitchen Display System (KDS)</strong>
                    <p>Orders displayed instantly in the kitchen for faster prep.</p>
                  </div>
                </div>

                <div className="feature-item">
                  <span className="feature-icon">📊</span>
                  <div className="feature-text">
                    <strong>Admin Dashboard</strong>
                    <p>Manage menu, orders, tables, and view detailed reports.</p>
                  </div>
                </div>

                <div className="feature-item">
                  <span className="feature-icon">💬</span>
                  <div className="feature-text">
                    <strong>Customer Feedback</strong>
                    <p>Collect reviews after order completion to improve service.</p>
                  </div>
                </div>

                <div className="feature-item">
                  <span className="feature-icon">🏢</span>
                  <div className="feature-text">
                    <strong>Multi-Branch Support</strong>
                    <p>Manage multiple franchise operations under one system.</p>
                  </div>
                </div>
              </div>

              <div className="modal-footer-embedded">
                <button className="btn-secondary" onClick={handleClose}>Cancel</button>
                <button className="btn-primary" onClick={handleRequestClick}>Request Access</button>
              </div>
            </>
          )}
        </div>

        {/* Removed fixed footer as it is now conditional inside body or embedded */}
      </div>
    </div>
  )
}

export default FranchiseDetailsModal
